/**
 * «godnokfestivalen.no» ER EN NETTADRESSE (#16c).
 *
 * =================================================================================================
 * Atle, QA-runde 12: feltet svarte «Formatet er ikke gyldig» på adressen til sin egen festival,
 * fordi den manglet `https://`. «Det skal ikke ha noe å si.»
 *
 * Kontrakten er `z.url()`, og den har rett: det som LAGRES må være en hel adresse, ellers kan
 * ingenting lenke til den. Feilen var at kravet sto i skjemaet i stedet for i lagringen. Så
 * skjemaet normaliserer på vei ut — `register(..., { setValueAs: normalizeWebsiteUrl })` kjører
 * FØR resolveren — og `z.url()` får aldri se den halve adressen den ville avvist.
 *
 * =================================================================================================
 * FIRE REGLER, OG DE TRE SISTE ER DET SOM GJØR DEN TRYGG.
 *
 *   1. MANGLER SKJEMA, SÅ LEGGES `https://` PÅ. Det er hele saken Atle meldte.
 *   2. ET SKJEMA SOM STÅR DER, RØRES IKKE. `http://intranett.lokalt` blir stående som `http` —
 *      å «oppgradere» den ville vært å påstå at serveren har et sertifikat, og en lenke som ikke
 *      åpner er verre enn en som ikke er kryptert. Atle ba om at manglende skjema skal godtas,
 *      ikke om at et valgt skjema skal overstyres.
 *   3. EN AVSLUTTENDE SKRÅSTREK UTEN STI FJERNES. `new URL()` gjør «x.no» til «https://x.no/», og
 *      den skråstreken er en forskjell ingen skrev — den ville gitt en PATCH hver gang skjemaet ble
 *      lagret uten at noen rørte feltet.
 *   4. DET SOM IKKE LAR SEG TOLKE, SENDES VIDERE SOM DET ER. En normalisering som svelger søppel
 *      gjør feilmeldingen til gjetning: `z.url()` navngir feltet, og det gjør den bare hvis den
 *      faktisk får se verdien.
 *
 * =================================================================================================
 * DENNE FILA SKAL SLÅS SAMMEN MED DEN FELLES.
 *
 * QA-punktet sier «gjelder alle URL-felt i appen, via én felles validering og normalisering», og
 * den felles utgaven bygges i `packages/shared` + `apps/web/src/lib` av en annen agent i samme
 * runde. Da den ble skrevet fantes den ikke ennå, og et felt som blokkerer oppretting kan ikke
 * vente på en nabofil. Formen her er valgt for å kunne erstattes av ett `import`: én ren funksjon,
 * ingen React, ingen i18n, ingen avhengighet til festivalen.
 */

/** `https:`, `mailto:`, `ftp:` — anything the reader deliberately put in front. */
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * What a website field hands the contract: a whole address, or nothing.
 *
 * Typed loosely because it is used as react-hook-form's `setValueAs`, which is handed whatever
 * the DOM holds — a string in practice, `undefined` for a control that never rendered.
 */
export function normalizeWebsiteUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  const text = raw.trim();
  if (text === '') return null;

  // A pasted "//example.no" is protocol-relative and means the same thing; the slashes are noise
  // once a scheme is being added anyway.
  const withScheme = HAS_SCHEME.test(text) ? text : `https://${text.replace(/^\/+/, '')}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    // Rule 4: unparseable goes on to the schema, which says so about this field by name.
    return withScheme;
  }

  const rendered = url.toString();
  // Rule 3. Only the bare slash `new URL` adds — a path somebody typed keeps its own.
  return url.pathname === '/' && url.search === '' && url.hash === ''
    ? rendered.replace(/\/$/, '')
    : rendered;
}
